import * as React from 'react';
import { isValueSelected } from '../../utils';
import { useForkRef } from '../../hooks';
import { useAutocompleterContext } from '../Autocompleter/useAutocompleter';
import { Divider, DividerProps } from '../Divider/Divider';
import { AutocompleterCategory, AutocompleterCategoryProps } from './AutocompleterCategory';
import { AutocompleterList, AutocompleterListProps } from './AutocompleterList';
import { AutocompleterOption, AutocompleterOptionProps } from './AutocompleterOption';
import { Stacker, StackerProps } from '../Stacker';

export interface AutocompleterResultsProps extends StackerProps {
    /**
     * The child components.
     */
    children?: (React.ReactElement<AutocompleterCategoryProps | DividerProps> | null)[];

    /**
     * The unique id of the list, which should also be provided to the `AutocompleterField` component which owns this list.
     * Used for accessibility markup.
     */
    id: string;

    /**
     * The text that will be used for the component's `aria-label` attribute.
     */
    label?: string;

    /**
     * Sets option index for special cases.
     */
    startIndex?: number;
}

export function AutocompleterResults(props: Readonly<AutocompleterResultsProps>): React.JSX.Element {
    const { children, id, label, startIndex = 1, ref, ...other } = props;
    const { activeOptionIndex, selectedValue, registerResultsList, deregisterResultsList } = useAutocompleterContext();

    const rootRef = React.useRef<HTMLElement | null>(null);
    const handleRef = useForkRef(rootRef, ref);

    React.useEffect(() => {
        const el = rootRef.current;
        if (el) {
            registerResultsList?.(el);
        }

        return () => {
            if (el) {
                deregisterResultsList?.(el);
            }
        };
    }, [ref, registerResultsList, deregisterResultsList]);

    let optionIndex = startIndex;

    const renderList = (
        list: React.ReactElement<AutocompleterListProps>
    ): React.ReactElement<AutocompleterListProps> => {
        const listChildren = React.Children.map(list.props.children, (listChild) => {
            if (React.isValidElement(listChild)) {
                if (listChild.type === AutocompleterOption) {
                    // This is an `AutocompleterOption`
                    const option = listChild as React.ReactElement<AutocompleterOptionProps>;

                    const parentIndex = optionIndex;
                    optionIndex += 1;

                    const { value } = option.props;

                    // An `AutocompleterOption` can contain a single `AutocompleterList`.
                    let nestedList;
                    if (React.Children.count(option.props.children) === 1) {
                        const optionChild = React.Children.only(option.props.children);
                        if (React.isValidElement(optionChild) && optionChild.type === AutocompleterList) {
                            nestedList = renderList(optionChild);
                        }
                    }

                    return React.cloneElement(option, {
                        index: parentIndex,
                        active: parentIndex === activeOptionIndex,
                        selected: isValueSelected(value, selectedValue),
                        children: nestedList,
                    });
                }

                if (listChild.type === Divider) {
                    // This is a list `divider`
                    const divider = listChild as React.ReactElement<DividerProps>;
                    return React.cloneElement(divider, {
                        component: 'li',
                    });
                }
            }
            return null;
        });
        return React.cloneElement(list, {
            children: listChildren,
        });
    };

    return (
        <Stacker component="ul" ref={handleRef} id={id} aria-label={label} {...other}>
            {React.Children.map(children, (resultsChild) => {
                if (React.isValidElement(resultsChild)) {
                    if (resultsChild.type === AutocompleterCategory) {
                        // This is an `AutocompleterCategory`
                        const category = resultsChild as React.ReactElement<AutocompleterCategoryProps>;

                        // An `AutocompleterCategory` can contain a single `AutocompleterList`.
                        let list;
                        if (React.Children.count(category.props.children) === 1) {
                            const categoryChild = React.Children.only(category.props.children);
                            if (React.isValidElement(categoryChild) && categoryChild.type === AutocompleterList) {
                                list = renderList(categoryChild);
                            }
                        }

                        return React.cloneElement(category, {
                            children: list,
                        });
                    }

                    if (resultsChild.type === Divider) {
                        // This is a category `divider`
                        const divider = resultsChild as React.ReactElement<DividerProps>;
                        return React.cloneElement(divider, {
                            component: 'li',
                        });
                    }
                }
                return null;
            })}
        </Stacker>
    );
}
